// src/utils/roleTestUtils.js
// Utilidades para testing del sistema de roles

import { 
  getUserRole, 
  hasAccess, 
  isAdminTenant, 
  clearRoleData, 
  getCognitoGroups, 
  getTenantId,
  extractUserRole,
  ALLOWED_ROLES 
} from './roleUtils';

// Función para ejecutar una prueba completa del sistema de roles
export const runRoleSystemTest = async () => {
  // console.log('🧪 [TEST] Iniciando prueba completa del sistema de roles...');
  
  try {
    // 1. Obtener tenant_id
    // console.log('📋 [TEST] Paso 1: Obteniendo tenant_id...');
    const tenantId = await getTenantId();
    // console.log(`   Resultado: ${tenantId ? `✅ ${tenantId}` : '❌ No encontrado'}`);
    
    // 2. Obtener grupos de Cognito
    // console.log('📋 [TEST] Paso 2: Obteniendo grupos de Cognito...');
    const groups = await getCognitoGroups();
    // console.log(`   Resultado: ${groups.length > 0 ? `✅ ${groups.length} grupos` : '❌ Sin grupos'}`);
    // console.log('   Grupos:', groups);
    
    // 3. Extraer rol del usuario
    // console.log('📋 [TEST] Paso 3: Extrayendo rol del usuario...');
    const extractedRole = extractUserRole(groups, tenantId);
    // console.log(`   Resultado: ${extractedRole ? `✅ ${extractedRole}` : '❌ Sin rol'}`);
    
    // 4. Obtener rol completo
    // console.log('📋 [TEST] Paso 4: Obteniendo rol completo...');
    const userRole = await getUserRole();
    // console.log(`   Resultado: ${userRole ? `✅ ${userRole}` : '❌ Sin rol'}`);
    
    // 5. Verificar acceso
    // console.log('📋 [TEST] Paso 5: Verificando acceso...');
    const hasAccessResult = await hasAccess(ALLOWED_ROLES);
    // console.log(`   Resultado: ${hasAccessResult ? '✅ Acceso concedido' : '❌ Acceso denegado'}`);
    
    // 6. Verificar si es admin
    // console.log('📋 [TEST] Paso 6: Verificando si es admin...');
    const isAdmin = await isAdminTenant();
    // console.log(`   Resultado: ${isAdmin ? '✅ Es admin_tenant' : '❌ No es admin_tenant'}`);
    
    // 7. Resumen
    // console.log('📋 [TEST] Resumen de la prueba:');
    // console.log(`   - Tenant ID: ${tenantId || 'No encontrado'}`);
    // console.log(`   - Grupos: ${groups.length} encontrados`);
    // console.log(`   - Rol: ${userRole || 'No detectado'}`);
    // console.log(`   - Acceso: ${hasAccessResult ? 'Concedido' : 'Denegado'}`);
    // console.log(`   - Admin: ${isAdmin ? 'Sí' : 'No'}`);
    
    return {
      tenantId,
      groups,
      userRole,
      hasAccess: hasAccessResult,
      isAdmin,
      success: true
    };
    
  } catch (error) {
    // console.error('💥 [TEST] Error durante la prueba:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Función para simular diferentes roles (solo para testing)
export const simulateRole = (mockGroups, mockTenantId) => {
  // console.log('🎭 [TEST] Simulando rol con datos mock...');
  // console.log('   Grupos mock:', mockGroups);
  // console.log('   Tenant ID mock:', mockTenantId);
  
  const role = extractUserRole(mockGroups, mockTenantId);
  // console.log('   Rol simulado:', role);
  
  return role;
};

// Función para probar todos los roles permitidos
export const testAllRoles = () => {
  // console.log('🧪 [TEST] Probando todos los roles permitidos...');
  
  const mockTenantId = 'REFURBI';
  const results = {};
  
  ALLOWED_ROLES.forEach(role => {
    const mockGroups = [`${mockTenantId}#${role}`];
    const extractedRole = extractUserRole(mockGroups, mockTenantId);
    results[role] = extractedRole === role;
    // console.log(`   ${role}: ${extractedRole === role ? '✅' : '❌'}`);
  });
  
  return results;
};

// Función para verificar la estructura de grupos
export const analyzeGroupStructure = (groups) => {
  // console.log('🔍 [TEST] Analizando estructura de grupos...');
  
  const analysis = {
    total: groups.length,
    withTenant: 0,
    withRoles: 0,
    validStructure: 0,
    roles: [],
    tenantIds: [],
    invalid: []
  };
  
  groups.forEach(group => {
    if (typeof group === 'string') {
      if (group.includes('#')) {
        const parts = group.split('#');
        if (parts.length === 2) {
          const [tenantId, role] = parts;
          analysis.validStructure++;
          
          if (tenantId && !analysis.tenantIds.includes(tenantId)) {
            analysis.tenantIds.push(tenantId);
          }
          
          if (role && !analysis.roles.includes(role)) {
            analysis.roles.push(role);
          }
          
          if (ALLOWED_ROLES.includes(role)) {
            analysis.withRoles++;
          }
        } else {
          analysis.invalid.push(group);
        }
        analysis.withTenant++;
      } else {
        analysis.invalid.push(group);
      }
    } else {
      analysis.invalid.push(group);
    }
  });
  
  // console.log('   Análisis:', analysis);
  return analysis;
};

// Función para debugging específico
export const debugRoleDetection = async () => {
  // console.log('🔧 [DEBUG] Debugging detección de roles...');
  
  try {
    // Obtener datos raw
    const tenantId = await getTenantId();
    const groups = await getCognitoGroups();
    
    // console.log('🔧 [DEBUG] Datos raw:');
    // console.log('   Tenant ID:', tenantId);
    // console.log('   Grupos:', groups);
    // console.log('   Tipo tenant ID:', typeof tenantId);
    // console.log('   Tipo grupos:', typeof groups);
    // console.log('   Es array grupos:', Array.isArray(groups));
    
    // Analizar estructura
    const analysis = analyzeGroupStructure(groups);
    
    // Probar métodos de detección
    // console.log('🔧 [DEBUG] Probando métodos de detección:');
    
    ALLOWED_ROLES.forEach(role => {
      // console.log(`   Rol: ${role}`);
      
      // Método 1: Búsqueda exacta
      const exactMatch = `${tenantId}#${role}`;
      const hasExact = groups.includes(exactMatch);
      // console.log(`     Búsqueda exacta (${exactMatch}): ${hasExact ? '✅' : '❌'}`);
      
      // Método 2: Búsqueda parcial
      const hasPartial = groups.some(group => group.includes(role));
      // console.log(`     Búsqueda parcial (contiene "${role}"): ${hasPartial ? '✅' : '❌'}`);
      
      if (hasPartial) {
        const foundGroup = groups.find(group => group.includes(role));
        // console.log(`     Grupo encontrado: ${foundGroup}`);
      }
    });
    
    return { tenantId, groups, analysis };
    
  } catch (error) {
    // console.error('💥 [DEBUG] Error en debugging:', error);
    return { error: error.message };
  }
};

// Función para limpiar y recargar datos
export const resetAndReload = async () => {
  // console.log('🔄 [TEST] Limpiando y recargando datos de roles...');
  
  // Limpiar datos
  clearRoleData();
  
  // Esperar un poco
  await new Promise(resolve => setTimeout(resolve, 100));
  
  // Recargar datos
  const newRole = await getUserRole();
  // console.log('   Nuevo rol cargado:', newRole);
  
  return newRole;
};

// Función para exportar todas las funciones de test
export const roleTestSuite = {
  runRoleSystemTest,
  simulateRole,
  testAllRoles,
  analyzeGroupStructure,
  debugRoleDetection,
  resetAndReload
};

// Hacer disponible en window para debugging desde consola
if (typeof window !== 'undefined') {
  window.roleTestSuite = roleTestSuite;
  // console.log('🧪 [TEST] Suite de testing disponible en window.roleTestSuite');
} 